/* ═══════════════════════════════════════════════════════════════
   Specs widget — card face of the project's specs (was the "Specs"
   tab in the old combined Projects page's detail dialog). A Forma
   strip (folded in from the deleted forma-widget.tsx, Widgets R2
   Decision 5) sits at the top when the viewer holds `formas.view`;
   below it, chips (sections · issued · not issued) → rows (CSI # ·
   title · latest issuance pill) → "+ Add spec" row → "View all N".
   The expand action opens SpecsTool as a full-bleed takeover — the ONE
   place the whole toolbar + table + export/import flow lives.
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useState } from 'react';
import { Maximize2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { SearchableSelect } from '../../../components/SearchableSelect';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { WidgetFrame, WidgetActionButton, WList } from '../../../components/widgets';
import type { WListRow } from '../../../components/widgets';
import { FormaService } from '../../../services/formaService';
import { hasPermission } from '../../../../shared/permissions';
import { realIssuances } from '../../../../shared/types';
import type { ProjectSpec } from '../../../../shared/types';
import { countChip, AddRow, COLOR_OK, COLOR_INFO } from './shared';
import type { Forma, ProjectWidgetCtx, ProjectWidgetDef } from './context';
import { SpecsTool } from './specs-tool';
import { StatusBadge } from '../../../components/StatusBadge';
import { isProjectSpecPending } from '../../../../shared/spec-publish';
import { pendingFormaCount, summarizeBySpec } from '../../../review/feedSummary';
import { useProjectSyncFeed } from './useProjectSyncFeed';

function latestIssuanceName(s: ProjectSpec): string {
  return realIssuances(s.issuances).slice(-1)[0]?.name || '';
}

// ─── Forma strip — folded in from forma-widget.tsx (deleted; Widgets R2
// Decision 5). Every function survives verbatim: empty-state master
// picker + Place, Open in Designer, Remove (+ ConfirmDialog), Swap to a
// different master (+ ConfirmDialog), the multi-forma warning banner.
// `ctx.formas` is the host's PROJECT-scoped slice; the master picker
// needs the FULL firm-wide list, loaded locally (same service the old
// widget used). ───
function FormaStrip({ ctx }: { ctx: ProjectWidgetCtx }) {
  const [masters, setMasters] = useState<Forma[]>([]);
  const [formaPickerId, setFormaPickerId] = useState('');
  const [placingForma, setPlacingForma] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Forma | null>(null);
  const [swapPending, setSwapPending] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [swapping, setSwapping] = useState(false);

  // standard_configs rides { cat: 'formas' } server-side — Place is a
  // create, Remove is a delete; Swap does both, so it needs both keys.
  const canCreate = hasPermission(ctx.userPermissions, 'formas.create') && !ctx.locked;
  const canDelete = hasPermission(ctx.userPermissions, 'formas.delete') && !ctx.locked;
  const canSwap = canCreate && canDelete;

  async function loadMasters() {
    try {
      const all = await FormaService.list();
      setMasters(all.filter((f) => !f.projectId && !f.specId));
    } catch {
      setMasters([]);
    }
  }
  useEffect(() => {
    void loadMasters();
  }, []);

  const projectFormas = (ctx.formas.data || []).filter(
    (f) => f.projectId === ctx.project.id && !f.specId
  );
  const masterOptions = masters.map((m) => ({
    value: m.id,
    label: m.docSettings?.standardName || 'Untitled forma',
  }));

  async function placeFormaFromMaster(masterId: string) {
    if (!ctx.project.id || !masterId || placingForma) return;
    const master = masters.find((m) => m.id === masterId);
    if (!master) return;
    setPlacingForma(true);
    try {
      await FormaService.ensureProjectCopy(master, {
        id: ctx.project.id,
        number: ctx.project.projectNumber,
        name: ctx.project.name,
      });
      await ctx.formas.reload();
      setFormaPickerId('');
      ctx.toast('Forma placed in project');
    } catch (err: any) {
      ctx.toast('Failed to place forma: ' + (err?.message || 'Unknown error'), true);
    } finally {
      setPlacingForma(false);
    }
  }

  async function confirmRemove() {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await FormaService.delete(removeTarget.id);
      await ctx.formas.reload();
      ctx.toast('Project forma removed');
      setRemoveTarget(null);
    } catch (err: any) {
      ctx.toast('Failed to remove forma: ' + (err?.message || 'Unknown error'), true);
    } finally {
      setRemoving(false);
    }
  }

  async function confirmSwap() {
    const cur = projectFormas[0];
    if (!cur || !formaPickerId) return;
    setSwapping(true);
    try {
      await FormaService.delete(cur.id);
      await placeFormaFromMaster(formaPickerId);
      setSwapPending(false);
    } catch (err: any) {
      ctx.toast('Failed to swap forma: ' + (err?.message || 'Unknown error'), true);
    } finally {
      setSwapping(false);
    }
  }

  if (ctx.formas.status === 'loading') return null;

  const picker = (buttonLabel: string) => (
    <div className="flex max-w-[28.75rem] items-center gap-2">
      <div className="flex-1">
        <SearchableSelect
          value={formaPickerId}
          onChange={(v) => setFormaPickerId(v || '')}
          options={masterOptions}
          placeholder="Pick a master forma…"
          searchPlaceholder="Search master formas…"
        />
      </div>
      <Button
        onClick={() => void placeFormaFromMaster(formaPickerId)}
        disabled={!formaPickerId || placingForma}
      >
        {placingForma ? 'Placing…' : buttonLabel}
      </Button>
    </div>
  );

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border border-border p-3"
      style={{ background: 'var(--sa)' }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 700,
          color: 'var(--tm)',
          textTransform: 'uppercase',
          letterSpacing: '.08em',
        }}
      >
        Forma
      </div>
      {projectFormas.length === 0 ? (
        <div className="flex flex-col gap-3">
          <div className="text-sm text-subtle">
            {masters.length === 0
              ? 'No master formas exist yet — create one in Master Formas first.'
              : canCreate
                ? 'Place a copy of a master forma in this project — every spec export uses it.'
                : "You don't have permission to place a forma."}
          </div>
          {masters.length > 0 && canCreate && picker('Place in project')}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {projectFormas.length > 1 && (
            <div
              className="rounded-md px-3 py-2 text-sm text-warning"
              style={{ background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)' }}
            >
              A project has exactly one forma — remove the extras below.
            </div>
          )}
          {projectFormas.map((f) => (
            <div key={f.id} className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-bold text-text">
                {f.docSettings?.standardName || 'Untitled forma'}
              </span>
              {/* The shared draft holds unpublished edits (appendix item 9):
                  every export still uses the published forma. */}
              {f.draftDirty && <StatusBadge status="draft" label="Pending publish" />}
              <span style={countChip(COLOR_INFO)}>
                {masters.find((m) => m.id === f.sourceFormaId)
                  ? `Forked from ${masters.find((m) => m.id === f.sourceFormaId)?.docSettings?.standardName}`
                  : 'Project copy'}
              </span>
              <div className="flex flex-1 justify-end gap-2">
                <Button
                  size="sm"
                  onClick={() => ctx.onOpenForma?.(f.id)}
                  disabled={!ctx.onOpenForma}
                >
                  Open in Designer
                </Button>
                {canDelete && (
                  <Button size="sm" variant="outline" onClick={() => setRemoveTarget(f)}>
                    Remove
                  </Button>
                )}
              </div>
            </div>
          ))}
          {projectFormas.length === 1 && canSwap && (
            <div className="flex flex-wrap items-center gap-2 border-t border-border pt-2">
              <span className="text-sm text-subtle">Swap to a different master:</span>
              <div className="min-w-[16rem] flex-1">
                <SearchableSelect
                  value={formaPickerId}
                  onChange={(v) => setFormaPickerId(v || '')}
                  options={masterOptions.filter((o) => o.value !== projectFormas[0].sourceFormaId)}
                  placeholder="Pick a different master…"
                  searchPlaceholder="Search master formas…"
                />
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={!formaPickerId || placingForma}
                onClick={() => setSwapPending(true)}
              >
                {placingForma ? 'Swapping…' : 'Swap'}
              </Button>
            </div>
          )}
        </div>
      )}
      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(o) => !o && setRemoveTarget(null)}
        title={`Remove "${removeTarget?.docSettings?.standardName || 'this forma'}" from this project?`}
        description="The master it was forked from is untouched. Exports fall back to no forma until you place another."
        confirmLabel="Remove"
        busy={removing}
        onConfirm={() => void confirmRemove()}
      />
      <ConfirmDialog
        open={swapPending}
        onOpenChange={(o) => !o && setSwapPending(false)}
        title={`Replace "${projectFormas[0]?.docSettings?.standardName || 'the current forma'}"?`}
        description="Its project-specific tweaks are deleted and a fresh copy of the selected master takes over."
        confirmLabel="Swap"
        busy={swapping}
        onConfirm={() => void confirmSwap()}
      />
    </div>
  );
}

function SpecsCard({ ctx }: { ctx: ProjectWidgetCtx }) {
  // Master updates per section + the forma badge. A feed that fails to load
  // only drops the badges — the specs list itself never blanks.
  const { feed } = useProjectSyncFeed(ctx.project.id);
  const updates = summarizeBySpec(feed);
  const formaUpdates = pendingFormaCount(feed);
  // The forma badge is a door (D9): it opens the project forma in Review.
  const formaUpdateTarget =
    feed?.rows.find((r) => r.targetKind === 'project_forma' && r.status === 'pending')?.targetId ??
    null;
  const slice = ctx.projectSpecs;
  const specs = slice.data || [];
  const status =
    slice.status === 'loading' ? 'loading' : slice.status === 'error' ? 'error' : 'ready';

  const actions = (
    <WidgetActionButton
      icon={Maximize2}
      label="Open specs"
      onClick={() => ctx.openTakeover('specs')}
    />
  );

  const sorted = specs.slice().sort((a, b) => (a.csiNumber || '').localeCompare(b.csiNumber || ''));
  const issuedCount = specs.filter((s) => latestIssuanceName(s)).length;
  const notIssuedCount = specs.length - issuedCount;
  const showFormaStrip = hasPermission(ctx.userPermissions, 'formas.view');
  const canAddSpec = ctx.can.viewCompile && !ctx.locked;

  // ONE badge per section (SYNC D8): changes waiting from a master win;
  // otherwise "Draft" = edited since the last Publish.
  const sectionBadge = (s: ProjectSpec) => {
    const n = updates.get(s.id)?.count ?? 0;
    if (n > 0) {
      return (
        <StatusBadge
          status="updates"
          label={`${n} ${n === 1 ? 'update' : 'updates'}`}
          variant="info"
        />
      );
    }
    return isProjectSpecPending(s) ? <StatusBadge status="draft" /> : null;
  };

  // Status stays 'ready' (never 'empty') once loaded — even with zero
  // specs — so the Forma strip above still renders. WidgetFrame's 'empty'
  // status would swap the WHOLE body for a bare EmptyState, hiding a
  // placed forma along with the empty specs message.
  return (
    <WidgetFrame
      title="Specs"
      required
      form="list"
      status={status}
      actions={actions}
      meta={
        formaUpdates > 0 ? (
          <span className="flex items-center gap-2">
            <StatusBadge
              status="updates"
              label={`Forma updates · ${formaUpdates}`}
              variant="info"
            />
            {ctx.onOpenForma && formaUpdateTarget && (
              <Button
                size="sm"
                variant="outline"
                aria-label={`Review ${formaUpdates} forma ${formaUpdates === 1 ? 'update' : 'updates'}`}
                title="Review the changes from this forma's master"
                onClick={() => ctx.onOpenForma?.(formaUpdateTarget, { review: true })}
              >
                Review
              </Button>
            )}
          </span>
        ) : undefined
      }
      error={{
        message: slice.error || 'Could not load specs',
        onRetry: () => void slice.reload(),
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {showFormaStrip && <FormaStrip ctx={ctx} />}
        {specs.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
              padding: 'var(--space-2)',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--tx)' }}>
              No specs yet
            </div>
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
              Pull master sections in to start building the project manual.
            </div>
            {canAddSpec && (
              <Button
                size="sm"
                style={{ alignSelf: 'center' }}
                onClick={() => ctx.navigate?.({ view: 'compile', projectId: ctx.project.id })}
              >
                Add specs
              </Button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <span style={countChip(COLOR_INFO)}>{specs.length} sections</span>
            {issuedCount > 0 && <span style={countChip(COLOR_OK)}>{issuedCount} issued</span>}
            {notIssuedCount > 0 && (
              <span style={countChip('var(--tm)')}>{notIssuedCount} not issued</span>
            )}
          </div>
        )}
        {specs.length > 0 && (
          <WList
            rows={sorted.map(
              (s): WListRow => ({
                id: s.id,
                lead: (
                  <span className="font-mono text-xs font-semibold text-primary">
                    {s.csiNumber || '—'}
                  </span>
                ),
                primary: s.title || 'Untitled',
                trailing:
                  latestIssuanceName(s) || sectionBadge(s) ? (
                    <span className="flex shrink-0 items-center gap-2">
                      {sectionBadge(s)}
                      {latestIssuanceName(s) && (
                        <span className="shrink-0 rounded-full bg-primary-soft px-2 py-0.5 text-xs font-semibold text-primary-text">
                          {latestIssuanceName(s)}
                        </span>
                      )}
                    </span>
                  ) : undefined,
                onClick: () => ctx.onOpenProjectSpec?.(s.id, ctx.project.id),
              })
            )}
            max={Math.max(sorted.length, 1)}
            footer={
              canAddSpec ? (
                <AddRow
                  label="Add spec"
                  onClick={() => ctx.navigate?.({ view: 'compile', projectId: ctx.project.id })}
                />
              ) : undefined
            }
          />
        )}
      </div>
    </WidgetFrame>
  );
}

export const specsWidget: ProjectWidgetDef = {
  id: 'specs',
  title: 'Specs',
  category: 'overview',
  requires: ['project_specs.view'],
  required: true,
  inputSurface: false,
  cols: { min: 3, max: 12 },
  defaultSize: 'standard',
  takeover: (ctx) => <SpecsTool ctx={ctx} />,
  takeoverTitle: 'Specs',
  render: (ctx) => <SpecsCard ctx={ctx} />,
};
